/* SchoolBannersRail — Phase 1 · Roster Sync polish
 * ─────────────────────────────────────────────────────────────────────
 * Horizontal rail of school-branded banner cards. Each card links to
 * `/school-network/{slug}` where visitors can browse that school's
 * full roster. Reads from `/api/school-network/list` (public) so no
 * auth needed. Silently returns null if no schools are populated,
 * so existing pages stay clean before any rosters are synced.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon glyph baked into each banner tile — a stadium-lights vibe.
const StadiumGlyph = ({ color }) => (
  <svg viewBox="0 0 120 120" className="w-full h-full opacity-30" aria-hidden>
    <defs>
      <radialGradient id="sg1" cx="50%" cy="30%" r="70%">
        <stop offset="0%" stopColor={color} stopOpacity="0.6" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect x="0" y="0" width="120" height="120" fill="url(#sg1)" />
    <path d="M0 90 Q60 60 120 90 L120 120 L0 120 Z" fill={color} fillOpacity="0.35" />
    <path d="M0 100 Q60 75 120 100 L120 120 L0 120 Z" fill={color} fillOpacity="0.55" />
    {/* stadium lights */}
    <circle cx="20" cy="20" r="2.5" fill={color} />
    <circle cx="60" cy="12" r="2.5" fill={color} />
    <circle cx="100" cy="20" r="2.5" fill={color} />
  </svg>
);

const SchoolBannersRail = ({ schools: providedSchools, title = 'Texas College Football', kicker = 'On The Network', networkBase = '/school-network', type = null, includeEmpty = false }) => {
  const [schools, setSchools] = useState(providedSchools || []);
  const [loading, setLoading] = useState(!providedSchools);

  useEffect(() => {
    if (providedSchools) return;
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (type) params.type = type;
        if (includeEmpty) params.include_empty = true;
        const res = await axios.get(`${API}/school-network/list`, { params }).catch(() => ({ data: [] }));
        if (!cancelled) setSchools(Array.isArray(res.data) ? res.data : (res.data?.schools || []));
      } catch {
        if (!cancelled) setSchools([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [providedSchools, type, includeEmpty]);

  if (loading) return null;
  // For HS type we intentionally keep empty-roster schools so the rail
  // can show a "Recruiting · Be first on the roster" card until the
  // first athlete claims. For colleges we still filter out empty.
  const active = includeEmpty
    ? (schools || [])
    : (schools || []).filter((s) => (s.athlete_count || (s.athlete_slugs || []).length) > 0);
  if (active.length === 0) return null;

  return (
    <section data-testid="school-banners-rail" className="py-2">
      {(title || kicker) && (
        <div className="flex items-end justify-between mb-4">
          <div>
            {kicker && <p className="font-italic text-blue-300 text-xs uppercase tracking-widest mb-1.5">{kicker}</p>}
            {title && <h2 className="font-display text-xl md:text-2xl uppercase tracking-wider text-white">{title}</h2>}
          </div>
        </div>
      )}
      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory no-scrollbar">
        {active.map((s) => {
          const color = s.primary_color || '#3b82f6';
          const count = s.athlete_count || (s.athlete_slugs || []).length;
          return (
            <Link
              key={s.slug}
              to={`${networkBase}/${s.slug}`}
              className="group flex-shrink-0 w-[240px] sm:w-[280px] snap-start rounded-2xl overflow-hidden ring-1 ring-white/10 hover:ring-white/40 transition relative"
              style={{
                background: `linear-gradient(135deg, ${color} 0%, ${color}88 60%, #0a0a0a 100%)`,
              }}
              data-testid={`school-banner-${s.slug}`}
            >
              {/* Backdrop glyph */}
              <div className="absolute inset-0 pointer-events-none">
                <StadiumGlyph color="#ffffff" />
              </div>

              {/* Logo circle */}
              <div className="relative p-4">
                <div
                  className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-md ring-2 ring-white/30 flex items-center justify-center overflow-hidden mb-3"
                  aria-hidden
                >
                  {s.logo_url ? (
                    <img src={s.logo_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display font-black text-xl text-white">
                      {(s.abbr || s.name || 'S').slice(0, 3).toUpperCase()}
                    </span>
                  )}
                </div>
                <p className="font-display text-lg leading-tight uppercase text-white line-clamp-2">
                  {s.name}
                </p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  {count > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-white/90 font-semibold">
                      <Users className="w-3.5 h-3.5" />
                      {count} athlete{count === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-white/15 text-white ring-1 ring-white/25">
                      Recruiting · Be first
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/80 font-bold group-hover:text-white transition">
                    {count > 0 ? 'View' : 'Claim'} <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default SchoolBannersRail;
